// Session-persistent feedback draft. Text + kind survive in sessionStorage
// (so a hard reload keeps the words); File objects can't be serialized, so
// they live in a module-level store that survives modal unmount/remount for
// the SPA session — on a hard reload only text/kind come back. Acceptable.

export type FeedbackKind = 'bug' | 'feature' | 'question'

export const MAX_FILES = 4
export const MAX_FILE_BYTES = 20 * 1024 * 1024

const KEY = 'innernet:feedback-draft'

let files: File[] = []
const listeners = new Set<() => void>()

function emit() {
  for (const fn of listeners) fn()
}

export function loadDraft(): { text: string; kind: FeedbackKind } {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (raw) {
      const d = JSON.parse(raw) as { text?: string; kind?: FeedbackKind }
      return {
        text: typeof d.text === 'string' ? d.text : '',
        kind: d.kind === 'feature' || d.kind === 'question' ? d.kind : 'bug',
      }
    }
  } catch {
    /* corrupt/unavailable storage → fresh draft */
  }
  return { text: '', kind: 'bug' }
}

export function saveDraft(text: string, kind: FeedbackKind) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ text, kind }))
  } catch {
    /* storage full/blocked — the in-memory state still carries the session */
  }
}

export function getDraftFiles(): File[] {
  return files
}

export function setDraftFiles(next: File[]) {
  files = next.slice(0, MAX_FILES)
  emit()
}

export type RejectReason = 'count' | 'size' | 'type' | null

/** Add pasted/dropped/browsed files, enforcing type, the 4-file cap and 20MB each. */
export function addDraftFiles(incoming: File[]): { added: number; rejected: RejectReason } {
  let rejected: RejectReason = null
  const next = [...files]
  for (const f of incoming) {
    if (!/^(image|video)\//.test(f.type)) {
      rejected = rejected ?? 'type'
      continue
    }
    if (f.size > MAX_FILE_BYTES) {
      rejected = rejected ?? 'size'
      continue
    }
    if (next.length >= MAX_FILES) {
      rejected = 'count'
      break
    }
    next.push(f)
  }
  const added = next.length - files.length
  if (added > 0) {
    files = next
    emit()
  }
  return { added, rejected }
}

export function clearDraft() {
  files = []
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
  emit()
}

export function subscribeDraftFiles(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
